import * as Ant from "antd";
import { ReadonlyArray } from "fp";

import React from "react";
import styled from "styled-components";

import * as Data from "~/Data";
import * as Driver from "~/Driver";
import * as GenericIcon from "~/Icon";
import * as Layout from "~/Layout";
import * as Log from "~/Log";
import { Remote } from "~/Remote";
import { Route } from "~/Route";
import { Search } from "~/Search";
import { Table as GenericTable } from "~/TableV2";
import { Time } from "~/Time";

import { CurrentAsset } from "./CurrentAsset";
import { DeviceType as DeviceType_ } from "./DeviceType";
import { Filter as Filter_ } from "./Filter";
import { HoursOfService } from "./HoursOfService";
import { Name as Name_ } from "./Name";
import { Status } from "./Status";
import { Timers } from "./Timer";

export function Table(match: Search.Match) {
  const filter = Table.Filter.use();
  const { summaries } = Remote.getOrElse(Log.Summaries.empty)(
    Log.Summaries.Query.use(Time.DateTime.toDayInterval(Time.DateTime.now()))
  );

  Driver.Filter.useFilteredDrivers();

  const pairings = Driver.Pairing.useFilteredPairings();
  const drivers = Driver.useFilteredStore();
  const data = pipe(
    drivers,
    Data.Store.toLatest,
    Data.Cache.toList(
      (id, driver): Table.Datum => ({
        ...driver,
        ...summaries.find(({ summary }) => id === summary.driver.id),
        pairing: pipe(
          pairings ?? {},
          (pairings) => pairings[id] ?? [],
          ReadonlyArray.sort(Driver.Pairing.descendingOrder),
          ([mostRecent]) => mostRecent
        ),
      })
    ),
    ReadonlyArray.filter(
      (datum) =>
        pipe(datum, Table.Datum.toSearch, Search.matches(match)) &&
        Table.Filter.applies(filter)(datum)
    )
  );

  return (
    <GenericTable
      rowKey={({ id }: Table.Datum) => id}
      loading={Remote.isLoading(drivers.latest)}
      columns={Table.columns(filter) as never}
      dataSource={data as never}
    />
  );
}

export namespace Table {
  export import DeviceType = DeviceType_;
  export import Filter = Filter_;
  export import Name = Name_;

  export const columns = (filter: Filter) =>
    [
      Status.column(),
      Name.column(filter),
      HoursOfService.column(filter),
      CurrentAsset.column(),
      DeviceType.column(filter),
      ...Timers.columns(),
      Action.column(),
    ] as const;

  export const useLogLink = (datum: Datum) =>
    Route.use(Log.Route.href({ driver: datum, ...Time.DateTime.now() }));

  export type Datum = Driver.Driver &
    Partial<Log.Summary> & { readonly pairing?: Driver.Pairing.Pairing };

  export namespace Datum {
    export const toSearch: Search.From<Datum> = ({ pairing, ...driver }) =>
      Search.join(
        Driver.toSearch(driver),
        pairing
          ? Driver.Pairing.toSearch(pairing)
          : Search.string("never paired")
      );
  }

  export namespace Action {
    export const column = () => ({ render });
    const render = (_: unknown, datum: Driver.Table.Datum) => {
      const menu = (
        <Ant.Menu>
          <Ant.Menu.Item
            onClick={
              Log.Route.use({ driver: datum, ...Time.DateTime.now() }).go
            }
          >
            <Icon>
              <GenericIcon.Calendar faded />
            </Icon>
            Today's log
          </Ant.Menu.Item>
          <Ant.Menu.Item onClick={Driver.Page.Route.use({ driver: datum }).go}>
            <Icon>
              <GenericIcon.Truck faded />
            </Icon>
            All logs
          </Ant.Menu.Item>
          {/* TODO: Enable during DVIR work */}
          {false && (
            <Ant.Menu.Item>
              <Icon>
                <GenericIcon.ClipboardListCheck faded />
              </Icon>
              DVIRs
            </Ant.Menu.Item>
          )}
          <Ant.Menu.Divider />
          <Ant.Menu.Item
            onClick={Driver.AddEdit.Route.use({ driver: datum }).go}
          >
            <Icon>
              <GenericIcon.Pencil faded />
            </Icon>
            Edit
          </Ant.Menu.Item>
        </Ant.Menu>
      );

      return (
        <Ant.Dropdown.Button
          overlay={menu}
          placement="bottomRight"
          icon={<GenericIcon.Ellipsis />}
        />
      );
    };

    const Icon = styled.span`
      width: 1.1em;
      margin-right: ${() => Layout.Inline.padding()};
    `;
  }

  export const Clickable = styled.span`
    cursor: pointer;
  `;

  export const Detail = styled.div`
    padding-top: ${() => Layout.Inline.padding()};
  `;
}
